import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Sparkles, X, Copy, Mic } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { anydeskTarget } from '../utils/remoteId';
import { useAuth } from '../contexts/AuthContext';
import CompanyLogo from './CompanyLogo';
import WebLoginHelper from './WebLoginHelper';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';

const CATEGORY_META = {
  smart_actions:  { emoji: '⚡', label: 'Quick actions' },
  navigation:     { emoji: '🧭', label: 'Navigation' },
  tickets:        { emoji: '🎫', label: 'Tickets' },
  companies:      { emoji: '🏢', label: 'Companies' },
  contacts:       { emoji: '👤', label: 'Contacts' },
  locations:      { emoji: '📍', label: 'Locations' },
  assets:         { emoji: '📦', label: 'Assets' },
  licenses:       { emoji: '🔑', label: 'Software Licenses' },
  infrastructure: { emoji: '🌐', label: 'Infrastructure' },
  printers:       { emoji: '🖨️', label: 'Printers' },
  cctv:           { emoji: '📹', label: 'CCTV' },
  monitors:       { emoji: '📡', label: 'Monitor' },
  probes:         { emoji: '📶', label: 'Network Probes' },
  vault:          { emoji: '🔐', label: 'Vault' },
  two_fa:         { emoji: '🔢', label: '2FA Codes' },
  invoices:       { emoji: '🧾', label: 'Invoices' },
  budgets:        { emoji: '💰', label: 'Budgets' },
  visits:         { emoji: '📅', label: 'Calendar Visits' },
  errands:        { emoji: '🚗', label: 'Field Tasks' },
  backup_jobs:    { emoji: '💾', label: 'Backup Monitor' },
  configurations: { emoji: '⚙️', label: 'Configurations' },
  vpn_users:      { emoji: '🔒', label: 'VPN Users' },
  users:          { emoji: '👥', label: 'Agents' },
  email_accounts: { emoji: '📧', label: 'Correo y Workspace' },
  drive_files:    { emoji: '📂', label: 'Drive expuesto' },
  kb:             { emoji: '📖', label: 'Knowledge Base' },
  projects:        { emoji: '📋', label: 'Projects' },
  spare_parts:     { emoji: '🔧', label: 'Spare Parts' },
  prepaid_lines:   { emoji: '📱', label: 'Prepaid Lines' },
  gbp_profiles:    { emoji: '🏷️', label: 'Google Business' },
  smart_links:     { emoji: '🔗', label: 'Smart Links (QR)' },
  meta_assets:     { emoji: '🟦', label: 'Meta (FB / IG / WhatsApp)' },
  website_reports: { emoji: '📊', label: 'Website reports' },
  racks:           { emoji: '🗄️', label: 'Racks' },
  rack_ports:      { emoji: '🔌', label: 'Bocas de patchera' },
  feedback_requests: { emoji: '💡', label: 'Feedback & Requests' },
  remote_connections: { emoji: '🖥️', label: 'Remote Access' },
  // Pseudo-category: a previously typed query string (from /search/history).
  // Deliberately NOT in CATEGORY_ORDER — it never comes back as a result group,
  // it's only rendered as its own section when the search box is empty.
  recent_query:   { emoji: '🔎', label: 'Recent searches' },
};

// Leading icon for a result row: prefer the entity's own image (asset photo,
// contact/agent avatar, company logo), then fall back to the company logo, then
// the category emoji. For an asset WITH a photo we also overlay a tiny company
// logo badge so you see the device AND who it belongs to at a glance.
function ResultIcon({ item, cat, emoji }) {
  const [err, setErr] = useState(false);
  if (cat === 'companies' && item.logo_url && !err) {
    return <CompanyLogo name={item.name || item.company_name} logoUrl={item.logo_url} size={20} />;
  }
  if ((cat === 'contacts' || cat === 'users') && item.avatar_url && !err) {
    return <img src={item.avatar_url} alt="" onError={() => setErr(true)}
      style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  if (item.image_url && !err) {
    return (
      <span style={{ position: 'relative', width: 20, height: 20, flexShrink: 0, display: 'inline-block' }}>
        <img src={item.image_url} alt="" onError={() => setErr(true)}
          style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover', border: '1px solid var(--c-border)', background: 'var(--c-surface-2)' }} />
        {item.company_logo_url && (
          <img src={item.company_logo_url} alt=""
            style={{ position: 'absolute', right: -3, bottom: -3, width: 11, height: 11, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--c-bg)', background: '#fff' }} />
        )}
      </span>
    );
  }
  if (item.company_logo_url && !err) {
    return <CompanyLogo name={item.name || item.company_name} logoUrl={item.company_logo_url} size={20} />;
  }
  return <span style={{ fontSize: 14, flexShrink: 0 }}>{emoji || '·'}</span>;
}

const CATEGORY_ORDER = [
  'smart_actions', 'navigation', 'tickets', 'companies', 'contacts', 'locations',
  'assets', 'licenses', 'infrastructure', 'printers', 'cctv', 'monitors', 'probes',
  'vault', 'two_fa', 'invoices', 'budgets', 'visits', 'errands',
  'backup_jobs', 'configurations', 'remote_connections', 'vpn_users', 'users', 'email_accounts', 'drive_files', 'kb', 'projects', 'spare_parts', 'prepaid_lines', 'gbp_profiles', 'smart_links', 'meta_assets', 'website_reports', 'racks', 'rack_ports', 'feedback_requests',
];

const STATUS_COLORS = {
  new: '#8b5cf6', open: '#3b82f6', pending: '#f59e0b', on_hold: '#ef4444',
  resolved: '#10b981', closed: '#6b7280',
  // Monitor / Probes
  healthy: '#10b981', warning: '#f59e0b', critical: '#ef4444', unknown: '#6b7280',
  online: '#10b981', offline: '#ef4444',
  // Invoice/Budget
  draft: '#6b7280', sent: '#3b82f6', approved: '#10b981', paid: '#10b981',
  rejected: '#ef4444', expired: '#6b7280',
  // Visits/Errands
  scheduled: '#3b82f6', completed: '#10b981', cancelled: '#6b7280',
  in_progress: '#f59e0b',
  // Backup monitor
  ok: '#10b981', paused: '#6b7280',
  // Licenses
  active: '#10b981', revoked: '#ef4444',
  // Projects
  planning: '#3b82f6',
};

// ── Match highlighting ────────────────────────────────────────────────────
// Bold the part of each result that matched what you typed, accent-insensitive
// (typing "fundacion" highlights "Fundació" in "Fundación"). We expand every
// query letter to a character class covering its accented variants and run that
// against the ORIGINAL string, so we never have to map indices through a
// normalized copy. Tokens < 2 chars are skipped (1-char highlights are noise).
const ACCENT_CLASS = {
  a: 'aàáâãäåāăą', e: 'eèéêëēĕėęě', i: 'iìíîïĩīĭįı',
  o: 'oòóôõöøōŏő', u: 'uùúûüũūŭůűų', c: 'cçćĉċč', n: 'nñńņňŉ',
  y: 'yýÿŷ', s: 'sśŝşš', g: 'gĝğġģ', z: 'zźżž',
};
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function buildHighlightRegex(queryStr) {
  const tokens = String(queryStr || '').trim().split(/\s+/).filter(t => t.length >= 2);
  if (!tokens.length) return null;
  const parts = tokens.map(tok =>
    [...tok].map(ch => {
      const base = ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
      if (ACCENT_CLASS[base]) {
        const cls = ACCENT_CLASS[base];
        return `[${cls}${cls.toUpperCase()}]`;
      }
      return escapeRegex(ch);
    }).join('')
  );
  try { return new RegExp(`(${parts.join('|')})`, 'gi'); } catch { return null; }
}

function highlightMatch(text, regex) {
  if (!regex || !text || typeof text !== 'string') return text;
  const out = [];
  let last = 0, m, count = 0;
  regex.lastIndex = 0;
  while ((m = regex.exec(text)) !== null && count < 100) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<strong key={count} style={{ color: 'var(--c-brand)', fontWeight: 700 }}>{m[0]}</strong>);
    last = m.index + m[0].length;
    if (m.index === regex.lastIndex) regex.lastIndex++; // guard against zero-length match loop
    count++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Defensive cleanup of the AI answer text: the model is told to write plain text
// and to use the offer_copy tool for copy buttons, but smaller models sometimes
// slip in markdown or a fake "[Copiar ...](javascript:...)" link. Strip those so
// the user never sees raw markup or a dead copy link (the real copy chips come
// from `copyables`).
function cleanAiMessage(s) {
  return String(s || '')
    .split('\n')
    .filter(line => !/\[[^\]]*\]\(\s*javascript:/i.test(line)) // drop fake copy-link lines
    .join('\n')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // remaining markdown links → just the text
    .replace(/\*\*([^*]+)\*\*/g, '$1')        // **bold**
    .replace(/__([^_]+)__/g, '$1')            // __bold__
    .replace(/^\s*[-*]\s*$/gm, '')            // empty leftover bullets
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function CommandPalette({ isOpen, onClose }) {
  const { t } = useTranslation();
  // Used to key the local "Recientes" list per user (see RECENTS_KEY below).
  const { user } = useAuth() || {};
  const userId = user?.id || null;
  // One-time intro banner for the new "Recently visited" / smart-ranking
  // features. Dismissed forever via localStorage flag after the user clicks
  // "Got it" or closes the palette having seen it. Bump the version suffix
  // (v1 → v2) any time we re-announce a new search capability.
  // v4: re-show once for everyone — announces the new AI write-actions phase.
  const INTRO_KEY = 'cmdk_intro_v4_seen';
  const [showIntro, setShowIntro] = useState(false);
  const dismissIntro = () => {
    try { localStorage.setItem(INTRO_KEY, '1'); } catch {}
    setShowIntro(false);
  };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  // Recent QUERY STRINGS (server-side, /search/history) — distinct from
  // `recents`, which are items the user opened. Fetched once per palette open.
  const [queryHistory, setQueryHistory] = useState([]);
  // Per-category "Ver más": categories whose full result list (up to 15 from
  // the server) is expanded past the default 5-row preview. Reset per query.
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  // Natural-language "Ask AI" mode (Phase 1: read-only / navigation).
  // null = not run for the current query. { loading } | { message, results } | { error }.
  // Fired explicitly via ⌘/Ctrl+Enter or the "Ask AI" row — never on every
  // keystroke, so normal fuzzy search stays instant and free.
  const [ai, setAi] = useState(null);
  // Conversation continuity for Ask AI: previous user/assistant turns, sent
  // back with each new query so follow-ups work ("y ponelo urgente"). Reset on
  // every palette open. Bounded server-side too.
  const aiHistoryRef = useRef([]);
  // Per-user opt-in state for the Ask AI feature. Off for everyone by default;
  // only admins see the toggle (so for now it's effectively owner-only). Fetched
  // from the server on open so the gate is authoritative, not client-trusted.
  const [aiPref, setAiPref] = useState({ enabled: false, canToggle: false });
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  // Response-race guard: every fired /search request takes a monotonic ticket.
  // Only the newest ticket may write to state, so a slow response for "cas"
  // can never land on top of the newer results for "casa".
  const searchSeqRef = useRef(0);
  // The query whose results are currently rendered. Used to reset the keyboard
  // highlight ONLY when the text actually changed — a late/duplicate response
  // must not yank the selection out from under the user's fingers.
  const lastAppliedQueryRef = useRef(null);
  // Usage logging: dedupe 'search' events so we log each distinct query once per
  // palette open (not on every keystroke).
  const loggedSearchesRef = useRef(new Set());

  // Quick Launch (web login): when set, the WebLoginHelper modal is open for this
  // vault entry id (opened via the "🌐 Login" smart-action row). Self-contained —
  // the helper handles its own fetch + PIN unlock.
  const [webLoginEntry, setWebLoginEntry] = useState(null);

  // ── Voice search (browser SpeechRecognition) ───────────────────────────────
  // Dictate the query instead of typing — drives the exact same search/launch
  // pipeline, so "anydesk flom" or "cloudflare flom" works by voice too. es-AR;
  // hidden entirely where unsupported (e.g. Firefox).
  const SpeechRec = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSupported = !!SpeechRec;
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const voiceSpot = useFeatureSpotlight('cmdk_voice');

  const stopVoice = () => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
  };

  const toggleVoice = () => {
    if (!speechSupported) return;
    if (recording) { stopVoice(); return; }
    if (voiceSpot.shouldShow) voiceSpot.dismiss();
    try {
      const rec = new SpeechRec();
      rec.lang = 'es-AR';
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (e) => {
        const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
        setQuery(transcript);
        setActiveIdx(0);
      };
      rec.onerror = (e) => {
        setRecording(false);
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          toast.error(t('cmdk.voice.denied'));
        }
      };
      rec.onend = () => { setRecording(false); try { inputRef.current?.focus(); } catch {} };
      recognitionRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setRecording(false);
    }
  };

  // Stop dictation when the palette closes/unmounts.
  useEffect(() => { if (!isOpen) stopVoice(); }, [isOpen]);

  // Escape cierra el buscador desde CUALQUIER lado, no sólo con el foco en el input.
  // handleKeyDown vive en el onKeyDown del <input>, así que apenas clickeabas un
  // resultado o un espacio vacío del panel el foco se iba y ESC dejaba de responder:
  // había que volver a clickear la barra para recién ahí poder cerrarlo. Lo reportó
  // Santi (vía Thiago, 31-ago-2026). Las flechas y Enter siguen en el input a
  // propósito: son navegación de la lista, no un atajo global.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);
  useEffect(() => () => stopVoice(), []);

  // ── Recently visited (per-user, localStorage) ─────────────────────────
  // Tracks items the user actually pressed enter on. Shown as a pinned section
  // when the search box is empty so common follow-ups (open same vault/ticket
  // again) take zero typing. Capped at 8; deduped by `${category}-${id}`.
  // Skips smart_actions and navigation — those are already in the empty-state
  // feed from the server and we don't want to double-show them.
  // Keyed per user: this is a shared-workstation app (agents swap seats), and a
  // global key leaked one agent's recently-opened tickets/vault entries into the
  // next agent's palette. Falls back to the bare key when auth hasn't hydrated
  // yet (user=null on the first render after a hard refresh).
  const RECENTS_KEY = userId ? `cmdk_recents_v1__${userId}` : 'cmdk_recents_v1';
  const RECENTS_MAX = 8;
  const RECENT_SKIP = new Set(['smart_actions', 'navigation']);

  const loadRecents = () => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  };

  const pushRecent = (entry) => {
    if (!entry || !entry._category || !entry.id) return;
    if (RECENT_SKIP.has(entry._category)) return;
    try {
      const key = `${entry._category}-${entry.id}`;
      const current = loadRecents().filter(r => `${r._category}-${r.id}` !== key);
      const next = [{ ...entry, _openedAt: Date.now() }, ...current].slice(0, RECENTS_MAX);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch { /* quota or serialization failure — ignore */ }
  };

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setActiveIdx(0);
      setRecents(loadRecents());
      lastAppliedQueryRef.current = null;
      // Recent search strings — one fetch per open, never per keystroke. Only
      // ever rendered while the box is empty, so there's nothing to refresh.
      setQueryHistory([]);
      api.get('/search/history')
        .then(({ data }) => setQueryHistory(Array.isArray(data?.history) ? data.history : []))
        .catch(() => setQueryHistory([]));
      aiHistoryRef.current = [];
      setAi(null);
      try { setShowIntro(localStorage.getItem(INTRO_KEY) !== '1'); } catch { setShowIntro(false); }
      api.get('/nl/preference')
        .then(({ data }) => setAiPref({ enabled: !!data.enabled, canToggle: !!data.canToggle }))
        .catch(() => setAiPref({ enabled: false, canToggle: false }));
      // Usage tracking: one 'open' event per palette open; reset per-session search dedupe.
      loggedSearchesRef.current = new Set();
      api.post('/search/event', { type: 'open' }).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isOpen) return;

    // `cancelled` covers the abort path (an aborted request rejects like any
    // other failure, and must NOT blank out the results of the newer query).
    let cancelled = false;
    const controller = new AbortController();
    const qAtSchedule = query;

    debounceRef.current = setTimeout(async () => {
      const seq = ++searchSeqRef.current;
      const isCurrent = () => !cancelled && seq === searchSeqRef.current;
      setLoading(true);
      try {
        const { data } = await api.get('/search', { params: { q: qAtSchedule }, signal: controller.signal });
        if (!isCurrent()) return; // a newer query already answered — drop this one
        setResults(data);
        // Only re-home the highlight when the TEXT changed. Resetting on every
        // response made the selection jump while the user was arrowing down.
        if (lastAppliedQueryRef.current !== qAtSchedule) {
          setActiveIdx(0);
          lastAppliedQueryRef.current = qAtSchedule;
        }
        // Usage tracking: log a 'search' once per distinct query per open.
        const qn = qAtSchedule.trim().toLowerCase();
        if (qn.length >= 2 && !loggedSearchesRef.current.has(qn)) {
          loggedSearchesRef.current.add(qn);
          api.post('/search/event', { type: 'search', query: qAtSchedule.trim() }).catch(() => {});
        }
      } catch {
        if (isCurrent()) setResults(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    }, query ? 150 : 0);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(debounceRef.current);
    };
  }, [query, isOpen]);

  // A new query invalidates any prior AI answer (it was for the old text) and
  // collapses any expanded categories (they belonged to the old result set).
  useEffect(() => { setAi(null); setExpandedCats(new Set()); }, [query]);

  const hasQueryNow = query.trim().length > 0;

  // Compiled once per render from the current query; null when empty. Data-row
  // labels, subtitles AND prefixes run through it so the matched substring is
  // bolded wherever it appears (e.g. searching a company name lights up the
  // company in a ticket's subtitle). Smart actions and navigation are
  // templated/keyword strings, not raw matches, so we skip them.
  const hlRegex = hasQueryNow ? buildHighlightRegex(query) : null;
  const hl = (text, cat) => {
    if (!hlRegex || !text || typeof text !== 'string' || cat === 'smart_actions' || cat === 'navigation') return text;
    return highlightMatch(text, hlRegex);
  };
  const renderLabel = (item, cat) => hl(getItemLabel(item, cat), cat);

  // `sections` + `allItems` are built lower down (after the getItem* helpers,
  // which the section builder calls to filter recents by query).

  const getItemPath = (item) => {
    switch (item._category) {
      case 'smart_actions':  return item.path;
      case 'navigation':     return item.path;
      case 'tickets':        return item.merged_into ? `/tickets/${item.merged_into}` : `/tickets/${item.id}`;
      case 'companies':      return `/companies?id=${item.id}`;
      case 'contacts':       return `/companies?contactId=${item.id}`;
      case 'locations':      return `/companies?id=${item.company_id}&locationId=${item.id}`;
      case 'assets':         return `/assets?id=${item.id}&tab=assets`;
      case 'licenses':       return `/assets?tab=licenses&id=${item.id}`;
      case 'infrastructure': return `/assets?tab=infrastructure&id=${item.id}`;
      case 'printers':       return `/supplies?printerId=${item.id}`;
      case 'cctv':           return `/cctv?id=${item.id}`;
      case 'monitors':       return `/monitor/${item.id}`;
      case 'probes':         return `/monitor?tab=probes&probeId=${item.id}`;
      case 'vault':          return `/vault?search=${encodeURIComponent(item.entry_name)}&entryId=${item.id}`;
      case 'two_fa':         return `/2fa-codes?id=${item.id}`;
      case 'invoices':       return `/invoicing?id=${item.id}`;
      case 'budgets':        return `/budgets/${item.id}/edit`;
      case 'visits':         return `/calendar?visitId=${item.id}`;
      case 'errands':        return `/field-tasks?id=${item.id}`;
      case 'backup_jobs':    return `/backup-monitor/${item.id}`;
      case 'configurations': return `/configurations?company_id=${item.company_id}&configName=${encodeURIComponent(item.name)}`;
      case 'vpn_users':      return `/vpn?search=${encodeURIComponent(item.username)}`;
      case 'users':          return `/agents?id=${item.id}`;
      case 'email_accounts': return `/email-accounts?id=${item.id}&search=${encodeURIComponent(item.email)}`;
      case 'drive_files':    return `/email-accounts?company_id=${item.company_id}&tab=drive&file=${encodeURIComponent(item.file_id)}`;
      case 'kb':             return `/kb/${item.slug || item.id}`;
      case 'projects':       return `/projects/${item.id}`;
      case 'spare_parts':    return `/assets?tab=spare-parts&id=${item.id}`;
      case 'prepaid_lines':  return `/prepaid-lines?id=${item.id}`;
      case 'gbp_profiles':   return `/google-business?id=${item.id}`;
      case 'smart_links':    return `/google-business?tab=links&link=${item.id}`;
      case 'meta_assets':    return `/google-business?tab=meta&portfolio=${item.portfolio_id}`;
      case 'website_reports': return `/website-reports?id=${item.id}`;
      case 'racks':          return `/racks?rack=${item.id}`;
      case 'rack_ports':     return `/racks?rack=${item.rack_id}&item=${item.item_id}&port=${item.id}`;
      case 'feedback_requests': return `/feedback?id=${item.id}`;
      case 'remote_connections': return `/remote-access?id=${item.id}`;
      default: return '/';
    }
  };

  const selectItem = (item) => {
    // A "Búsquedas recientes" row is a query string, not a destination: it
    // re-types the search instead of navigating anywhere.
    if (item && item._category === 'recent_query') {
      setQuery(item.query || '');
      setActiveIdx(0);
      try { inputRef.current?.focus(); } catch {}
      return;
    }
    pushRecent(item);
    // Fire-and-forget click log — drives per-user re-ranking on the next search.
    // The backend now accepts ANY string item_id (the column is TEXT), so
    // navigation rows (item.id === their path) and smart actions get logged too:
    // they're exactly the rows worth learning from ("hr" → /hr). They stay OUT of
    // the local "Recientes" list (RECENT_SKIP) on purpose — a nav row is already
    // one keystroke away, and a smart action is too ephemeral to pin.
    const logId = item?.id || item?.path;
    if (item && logId && item._category) {
      api.post('/search/click', {
        query: query.trim(),
        category: item._category,
        item_id: String(logId),
      }).catch(() => {});
    }
    // Special smart-action: fire the WinBox launcher in-place instead of
    // navigating. Mirrors InfrastructurePage.launchWinbox — POST
    // /launcher/winbox/token (the unified atlaunch:// launcher) for a one-shot
    // deeplink, then programmatic <a>.click() so Chrome treats the custom-scheme
    // open as user-initiated. Creds never touch the URL.
    if (item && item._action === 'launch_winbox' && item._device_id) {
      onClose();
      (async () => {
        try {
          const res = await api.post('/launcher/winbox/token', { device_id: item._device_id });
          if (!res.data?.deeplink) throw new Error('no deeplink');
          const a = document.createElement('a');
          a.href = res.data.deeplink;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { try { document.body.removeChild(a); } catch {} }, 2000);
          toast.success(t('infrastructure.winboxLaunching') || 'Abriendo WinBox…');
        } catch (err) {
          const msg = err?.response?.data?.error || err.message || 'launch failed';
          toast.error('WinBox: ' + msg);
        }
      })();
      return;
    }
    // Special smart-action: fire AnyDesk in-place instead of navigating. Mirrors
    // AnyDeskQuickButton.launch — POST /launcher/anydesk/token for a deeplink that
    // opens AnyDesk with the password pre-filled (atlaunch helper), falling back to a
    // plain anydesk:<id> if the helper isn't installed.
    if (item && item._action === 'launch_anydesk' && item._connection_id) {
      onClose();
      (async () => {
        toast.success(t('anydesk.launching') || 'Abriendo AnyDesk…');
        let deeplink = null;
        try {
          const res = await api.post('/launcher/anydesk/token', { connection_id: item._connection_id });
          deeplink = res.data?.deeplink || null;
        } catch { /* fall through to plain launch */ }
        const plain = () => { if (item._remote_id) window.open(`anydesk:${anydeskTarget(item._remote_id)}`, '_self'); };
        if (!deeplink) { plain(); return; }
        let helperTookOver = false;
        const onBlur = () => { helperTookOver = true; };
        window.addEventListener('blur', onBlur, { once: true });
        const a = document.createElement('a');
        a.href = deeplink; a.style.display = 'none';
        document.body.appendChild(a); a.click();
        setTimeout(() => { try { document.body.removeChild(a); } catch {} }, 2000);
        setTimeout(() => { window.removeEventListener('blur', onBlur); if (!helperTookOver && !document.hidden) plain(); }, 1500);
      })();
      return;
    }
    // Quick Launch web login: open the credential-handoff helper in-place instead
    // of navigating. The helper fetches /vault/entries/:id/web-login (PIN-gated +
    // audited) and opens the site + copy buttons. Keep the palette mounted so the
    // modal stays up; closing the helper closes both.
    if (item && item._action === 'launch_web' && item._vault_entry_id) {
      stopVoice();
      setWebLoginEntry(item._vault_entry_id);
      return;
    }
    onClose();
    navigate(getItemPath(item));
  };

  // ── Ask AI (natural-language command bar) ─────────────────────────────────
  const runAI = async () => {
    if (!aiPref.enabled) return; // feature off for this user
    const q = query.trim();
    if (!q || (ai && ai.loading)) return;
    setAi({ loading: true });
    try {
      const { data } = await api.post('/nl/command', { query: q, history: aiHistoryRef.current });
      // Feed the exchange back as context for the next question (text only).
      aiHistoryRef.current = [
        ...aiHistoryRef.current,
        { role: 'user', content: q },
        { role: 'assistant', content: String(data.message || '').slice(0, 1500) },
      ].slice(-8);
      setAi({
        loading: false,
        message: data.message,
        results: Array.isArray(data.results) ? data.results : [],
        copyables: Array.isArray(data.copyables) ? data.copyables : [],
        // Proposed write actions awaiting Confirmar. Local `state` per proposal:
        // 'pending' | 'working' | 'executed' | 'cancelled' | 'failed'.
        proposals: Array.isArray(data.proposals)
          ? data.proposals.map(p => ({ ...p, state: 'pending', resultMsg: null }))
          : [],
      });
    } catch (e) {
      setAi({ loading: false, error: e?.response?.data?.error || 'failed' });
    }
  };

  // Confirm/cancel a proposed action. Execution happens server-side with this
  // user's JWT; we just reflect the outcome on the card + a toast.
  const resolveProposal = async (proposalId, decision) => {
    const patch = (state, resultMsg = null) => setAi(prev => prev && ({
      ...prev,
      proposals: (prev.proposals || []).map(p => p.id === proposalId ? { ...p, state, resultMsg } : p),
    }));
    patch('working');
    try {
      if (decision === 'confirm') {
        const { data } = await api.post(`/nl/actions/${proposalId}/confirm`);
        patch('executed', data.message || null);
        toast.success(data.message || t('cmdk.ai.actionDone'));
        aiHistoryRef.current = [
          ...aiHistoryRef.current,
          { role: 'assistant', content: `(acción confirmada y ejecutada: ${data.summary || proposalId})` },
        ].slice(-8);
      } else {
        await api.post(`/nl/actions/${proposalId}/cancel`);
        patch('cancelled');
      }
    } catch (e) {
      const msg = e?.response?.data?.error || 'failed';
      patch('failed', msg);
      toast.error(`${t('cmdk.ai.actionFailed')}: ${msg}`);
    }
  };

  // Admin-only: flip the caller's own opt-in. Other agents are enabled by an
  // admin from Agents → modules. Turning off clears any visible AI answer.
  const toggleAi = async () => {
    const next = !aiPref.enabled;
    try {
      await api.post('/nl/preference', { enabled: next });
      setAiPref(p => ({ ...p, enabled: next }));
      if (!next) setAi(null);
    } catch { /* ignore — UI stays as-is */ }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); runAI(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(prev => Math.min(prev + 1, allItems.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(prev => Math.max(prev - 1, 0)); return; }
    if (e.key === 'Enter' && allItems[activeIdx]) { e.preventDefault(); selectItem(allItems[activeIdx]); }
  };

  useEffect(() => {
    const el = document.querySelector(`[data-cmd-idx="${activeIdx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!isOpen) return null;

  // Get the display label for an item
  const getItemLabel = (item, cat) => {
    if (cat === 'recent_query') return item.query || '';
    if (cat === 'smart_actions') {
      // Direct-asset actions (e.g. "open this PC") ship an explicit `label`;
      // company-filter actions use the "<module> → <company>" template.
      if (item.label) return `${item.module_emoji || ''} ${item.label}`.trim();
      return `${item.module_emoji || ''} ${item.module_label} → ${item.company_name}`.trim();
    }
    if (cat === 'users') return item.name || `${item.first_name} ${item.last_name}`;
    if (cat === 'email_accounts') return item.email;
    if (cat === 'budgets') return item.project_name || item.client_name;
    if (cat === 'vault') return item.entry_name;
    if (cat === 'two_fa') return item.label;
    if (cat === 'licenses') return item.software_name;
    if (cat === 'configurations') return item.name || item.filename;
    if (cat === 'remote_connections') return item.device_name || item.hostname || item.ip_address;
    if (cat === 'vpn_users') return item.username;
    if (cat === 'prepaid_lines') return item.phone_number || item.label;
    if (cat === 'gbp_profiles') return item.name;
    if (cat === 'smart_links') return `/r/${item.slug}`;
    if (cat === 'meta_assets') return item.name || item.username;
    if (cat === 'website_reports') return item.site_label;
    if (cat === 'racks') return item.name;
    if (cat === 'rack_ports') return item.label || item.destination || `Boca ${item.port_number}`;
    return item.subject || item.display_name || item.name || item.label || item.title || '';
  };

  // Get the prefix tag for an item (ticket number, asset tag, etc.)
  const getItemPrefix = (item, cat) => {
    if (cat === 'tickets') return `#${item.ticket_number}`;
    if (cat === 'assets') return item.asset_tag;
    if (cat === 'invoices') return item.invoice_number;
    if (cat === 'budgets') return item.budget_number;
    if (cat === 'errands') return item.errand_number ? `#${item.errand_number}` : null;
    if (cat === 'projects') return `P-${item.project_number}`;
    if (cat === 'infrastructure' || cat === 'printers' || cat === 'cctv' || cat === 'monitors')
      return item.ip_address;
    if (cat === 'probes') return item.probe_ip;
    if (cat === 'remote_connections') return item.remote_id || item.ip_address;
    if (cat === 'vault') return item.ip_address || item.username;
    if (cat === 'vpn_users') return item.local_address || item.remote_address;
    return null;
  };

  // Get the subtitle for an item
  const getItemSubtitle = (item, cat) => {
    if (cat === 'smart_actions') {
      if (item.subtitle) return item.subtitle;
      return `Open ${item.module_label.toLowerCase()} for ${item.company_name}`;
    }
    if (cat === 'navigation') return item.path;
    if (cat === 'users') return `${item.email} · ${item.role}`;
    if (cat === 'drive_files') return [item.is_public ? '🌐 público' : item.is_link_shared ? '🔗 enlace' : null, item.external_count ? `${item.external_count} externo(s)` : null, item.owner_email, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'infrastructure') return [item.device_type, item.model, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'remote_connections') return [item.tool_type, item.sector || item.floor, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'printers') return [item.make, item.model, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'cctv') return [item.device_type, item.model, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'monitors') return [item.check_type, item.hostname || item.domain_name, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'email_accounts') return [item.account_type, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'budgets') return [item.client_name, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'vault') return [item.entry_type, item.url, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'visits') return [item.visit_type, item.agent_name, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'probes') return [item.probe_hostname, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'two_fa') return [item.service, item.account_email, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'licenses') return [item.license_type, item.vendor, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'backup_jobs') return [item.backup_type, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'configurations') return [item.category, item.filename, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'vpn_users') return [item.service, item.profile, item.comment, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'locations') return [item.address, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'spare_parts') return [
      [item.brand, item.model].filter(Boolean).join(' '),
      item.sku && `SKU: ${item.sku}`,
      `stock: ${item.quantity}/${item.min_stock}`,
      item.company_name,
    ].filter(Boolean).join(' · ');
    if (cat === 'projects') return [item.company_name].filter(Boolean).join(' · ');
    if (cat === 'prepaid_lines') return [item.label, item.carrier, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'gbp_profiles') return [item.company_name, item.address, item.rating != null ? `${Number(item.rating).toFixed(1)}★` : null].filter(Boolean).join(' · ');
    if (cat === 'smart_links') return [item.label, item.company_name, item.vanity_url].filter(Boolean).join(' · ');
    if (cat === 'meta_assets') return [item.kind, item.username, item.company_name, item.portfolio_name].filter(Boolean).join(' · ');
    if (cat === 'website_reports') return item.company_name;
    if (cat === 'racks') return [item.location_name, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'rack_ports') return [`${item.item_name} #${item.port_number}`, item.destination && item.label ? item.destination : null, item.rack_name, item.company_name].filter(Boolean).join(' · ');
    if (cat === 'feedback_requests') return [item.type, item.source === 'claude_shipped' ? 'shipped by claude' : null].filter(Boolean).join(' · ');
    if (cat === 'tickets') {
      const parts = [item.company_name];
      if (item.merged_into) parts.push(`→ merged into #${item.merged_into_number}`);
      if (item.parent_ticket_id) parts.push(`split from #${item.parent_ticket_number}`);
      return parts.filter(Boolean).join(' · ');
    }
    return item.company_name || item.email || '';
  };

  // Get the status badge for an item
  const getItemStatus = (item, cat) => {
    if (cat === 'tickets' || cat === 'invoices' || cat === 'budgets' || cat === 'monitors'
        || cat === 'visits' || cat === 'errands' || cat === 'backup_jobs' || cat === 'licenses' || cat === 'projects'
        || cat === 'feedback_requests' || cat === 'prepaid_lines' || cat === 'gbp_profiles') return item.status;
    if (cat === 'probes') return item.heartbeat_status;
    if (cat === 'vpn_users') return item.is_online ? 'online' : 'offline';
    return null;
  };

  // ── Build the ordered sections the palette renders ────────────────────────
  // Both the rendered list AND keyboard navigation derive from `sections`, so
  // they can never drift out of sync.
  //   typing → ⭐ "Mejores resultados" (server best-bets) → category groups
  //   empty  → 🕘 "Recientes" (items opened) → 🔎 "Búsquedas recientes" → feed
  // Id-bearing rows are de-duped across sections so a record promoted to the
  // best-bets row doesn't appear twice; nav / smart-action / kb-slug rows
  // always pass through.
  const buildSections = () => {
    const secs = [];
    const seen = new Set();
    const take = (arr) => (arr || []).filter(it => {
      if (!it || !it.id || !it._category) return true; // nav/smart/kb-slug — never dedupe
      const key = `${it._category}-${it.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // 1) "Top" — cross-category best matches (match quality + click history);
    // only meaningful while typing.
    if (hasQueryNow && results?.top?.length) {
      const items = take(results.top.map(it => ({ ...it, _forYou: true })));
      if (items.length) secs.push({ key: 'for_you', emoji: '⭐', label: t('cmdk.topResults'), items });
    }
    // 2) Recents — ONLY on the empty state. While typing, the ⭐ best-bets row
    // above is the authoritative "what you probably meant"; a local list matched
    // by naive substring used to sit on top of it and outrank genuinely better
    // server results (and it can't see anything the user hasn't opened before).
    if (!hasQueryNow) {
      const recentItems = take(recents.map(r => ({ ...r, _isRecent: true })));
      if (recentItems.length) {
        secs.push({ key: 'recents', emoji: '🕘', label: t('cmdk.recentlyVisited'), items: recentItems, clearable: true });
      }
      // 2b) Recent SEARCHES (query strings). Below the opened-items list: an
      // item is one Enter away from where you were going, a query is one step back.
      if (queryHistory.length) {
        secs.push({
          key: 'recent_queries', emoji: '🔎', label: t('cmdk.recentSearches'),
          items: queryHistory
            .filter(h => h && h.query)
            .map(h => ({ ...h, _category: 'recent_query' })),
        });
      }
    }
    // 3) Category groups (includes smart_actions / navigation). Each data
    // category previews 5 rows; a "Ver más" row expands to the full server
    // list (up to 15) for that category.
    if (results) {
      for (const cat of CATEGORY_ORDER) {
        const arr = results[cat];
        if (!arr?.length) continue;
        const full = take(arr.map(it => ({ ...it, _category: cat })));
        if (!full.length) continue;
        const items = expandedCats.has(cat) ? full : full.slice(0, 5);
        secs.push({
          key: cat, emoji: CATEGORY_META[cat]?.emoji, label: CATEGORY_META[cat]?.label,
          items, category: cat, more: full.length - items.length,
        });
      }
    }
    return secs;
  };

  const sections = buildSections();
  const allItems = sections.flatMap(s => s.items);

  let globalIdx = -1;
  const hasQuery = hasQueryNow;

  const modal = (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '15vh' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      {/* El velo es un HIJO que cubre todo el fondo: el "e.target === e.currentTarget" del
          contenedor nunca se cumplía y tocar afuera no cerraba (Thiago, 24-sep). El velo
          cierra por sí mismo. */}
      <div onMouseDown={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />

      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 580, maxHeight: '60vh',
          background: 'var(--c-surface)', border: '1px solid var(--c-border)',
          borderRadius: 12, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--c-border)', gap: 10 }}>
          <Search size={18} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('cmdk.searchPlaceholder')}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              fontSize: 15, color: 'var(--c-text)', fontFamily: 'inherit',
            }}
          />
          {loading && <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>...</span>}
          {speechSupported && (
            <button
              ref={voiceSpot.anchorRef}
              type="button"
              onClick={toggleVoice}
              aria-label={t('cmdk.voice.tooltip')}
              title={t('cmdk.voice.tooltip')}
              className="btn btn-icon btn-ghost btn-sm"
              style={{
                flexShrink: 0,
                color: recording ? 'var(--c-danger, #ef4444)' : 'var(--c-text-muted)',
                animation: recording ? 'pulse 1.2s ease-in-out infinite' : 'none',
              }}
            >
              <Mic size={16} />
            </button>
          )}
          <kbd style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--c-surface-2)', color: 'var(--c-text-muted)', border: '1px solid var(--c-border)' }}>ESC</kbd>
        </div>

        {showIntro && (
          <div style={{
            margin: '8px 12px 0',
            padding: '10px 12px',
            background: 'color-mix(in srgb, var(--c-brand) 10%, var(--c-surface))',
            border: '1px solid color-mix(in srgb, var(--c-brand) 40%, var(--c-border))',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <Sparkles size={16} style={{ color: 'var(--c-brand)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--c-text)', lineHeight: 1.45 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {t('cmdk.intro.title') || 'Search just got smarter'}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 3, color: 'var(--c-text-muted)' }}>
                <li>🔤 {t('cmdk.intro.fuzzy') || 'Typo-tolerant: "fundacin" still finds "Fundación"'}</li>
                <li>✨ {t('cmdk.intro.highlight') || 'The part that matched what you typed is now highlighted'}</li>
                <li>🎯 {t('cmdk.intro.bestmatch') || 'Closest match comes first — not just alphabetical'}</li>
                <li>🎫 {t('cmdk.intro.ticketfilters') || 'Filter tickets inline: try "tickets abiertos flom" or "tickets urgentes"'}</li>
                <li>🤖 {t('cmdk.intro.askai') || 'NUEVO: preguntá en lenguaje natural y apretá ⌘↵ — la IA busca y te responde (dirección de un cliente, de qué se trata un ticket, etc.)'}</li>
                <li>⚡ {t('cmdk.intro.aiActions') || 'NUEVO: la IA también hace: "asignale el 7492 a Ariel y ponelo urgente", "nota interna en el 6475: ...". Siempre te muestra la acción y vos la confirmás antes de que se ejecute.'}</li>
              </ul>
              <button
                onClick={dismissIntro}
                style={{
                  marginTop: 8, fontSize: 11, fontWeight: 600,
                  background: 'var(--c-brand)', color: '#fff',
                  border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer',
                }}
              >
                {t('cmdk.intro.gotIt') || 'Got it'}
              </button>
            </div>
            <button
              onClick={dismissIntro}
              aria-label="Dismiss"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--c-text-muted)', padding: 2, flexShrink: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          {/* Ask AI — natural-language mode. Hint row when not yet run, panel after. */}
          {hasQuery && !ai && aiPref.enabled && (
            <div
              onClick={runAI}
              style={{
                margin: '2px 12px 6px', padding: '9px 12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8,
                background: 'color-mix(in srgb, var(--c-brand) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--c-brand) 35%, var(--c-border))',
              }}
            >
              <Sparkles size={16} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t('cmdk.ai.ask') || 'Preguntar a la IA'}: <span style={{ color: 'var(--c-text-muted)' }}>"{query}"</span>
              </div>
              <kbd style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--c-surface-2)', color: 'var(--c-text-muted)', border: '1px solid var(--c-border)', flexShrink: 0 }}>⌘↵</kbd>
            </div>
          )}

          {ai && (
            <div style={{
              margin: '2px 12px 8px', padding: '12px',
              borderRadius: 8,
              background: 'color-mix(in srgb, var(--c-brand) 7%, var(--c-surface))',
              border: '1px solid color-mix(in srgb, var(--c-brand) 35%, var(--c-border))',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: ai.loading ? 0 : 6 }}>
                <Sparkles size={15} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-brand)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {t('cmdk.ai.label') || 'Asistente IA'}
                </span>
                {ai.loading && <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{t('cmdk.ai.thinking') || 'pensando…'}</span>}
              </div>

              {ai.error && (
                <div style={{ fontSize: 13, color: 'var(--c-text-muted)' }}>
                  {ai.error === 'daily_cap'
                    ? (t('cmdk.ai.errorCap') || 'Se alcanzó el límite diario de gasto de IA.')
                    : ai.error === 'disabled'
                      ? (t('cmdk.ai.errorDisabled') || 'El asistente está desactivado.')
                      : ai.error === 'not_enabled'
                        ? (t('cmdk.ai.errorNotEnabled') || 'Activá el Asistente IA (beta) desde el pie del buscador.')
                        : (t('cmdk.ai.error') || 'No pude procesar la consulta. Probá de nuevo.')}
                </div>
              )}

              {ai.message && (
                <div style={{ fontSize: 13, color: 'var(--c-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{cleanAiMessage(ai.message)}</div>
              )}

              {/* Proposed write actions — NOTHING executes until Confirmar */}
              {ai.proposals?.length > 0 && (
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {ai.proposals.map(p => (
                    <div
                      key={p.id}
                      style={{
                        padding: '10px 12px', borderRadius: 8,
                        background: p.state === 'executed'
                          ? 'color-mix(in srgb, #10b981 10%, var(--c-surface))'
                          : p.state === 'cancelled' || p.state === 'failed'
                            ? 'var(--c-surface-2)'
                            : 'color-mix(in srgb, #f59e0b 10%, var(--c-surface))',
                        border: `1px solid ${p.state === 'executed' ? '#10b98155' : p.state === 'cancelled' || p.state === 'failed' ? 'var(--c-border)' : '#f59e0b66'}`,
                      }}
                    >
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, color: p.state === 'executed' ? '#10b981' : p.state === 'failed' ? '#ef4444' : p.state === 'cancelled' ? 'var(--c-text-muted)' : '#f59e0b' }}>
                        {p.state === 'executed' ? (t('cmdk.ai.actionDone') || 'Ejecutada')
                          : p.state === 'cancelled' ? (t('cmdk.ai.actionCancelled') || 'Cancelada')
                          : p.state === 'failed' ? (t('cmdk.ai.actionFailed') || 'Falló')
                          : (t('cmdk.ai.proposalTitle') || 'Acción propuesta — requiere tu confirmación')}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--c-text)', lineHeight: 1.45 }}>{p.summary}</div>
                      {p.resultMsg && p.state !== 'pending' && (
                        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 4 }}>{p.resultMsg}</div>
                      )}
                      {(p.state === 'pending' || p.state === 'working') && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            className="btn btn-success btn-sm"
                            disabled={p.state === 'working'}
                            onClick={(e) => { e.stopPropagation(); resolveProposal(p.id, 'confirm'); }}
                          >
                            {p.state === 'working' ? (t('common.loading') || '…') : (t('cmdk.ai.confirm') || 'Confirmar')}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={p.state === 'working'}
                            onClick={(e) => { e.stopPropagation(); resolveProposal(p.id, 'cancel'); }}
                          >
                            {t('cmdk.ai.cancel') || 'Cancelar'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {ai.copyables?.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ai.copyables.map((c, i) => (
                    <button
                      key={`cp-${i}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(c.value);
                          toast.success(t('cmdk.ai.copied') || 'Copiado');
                        } catch {
                          toast.error(t('common.error') || 'Error');
                        }
                      }}
                      title={c.value}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        padding: '5px 10px', borderRadius: 999,
                        background: 'var(--c-surface)', border: '1px solid var(--c-border)', color: 'var(--c-text)',
                      }}
                    >
                      <Copy size={12} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {ai.results?.length > 0 && (
                <div style={{ marginTop: 8, display: 'grid', gap: 2 }}>
                  {ai.results.map((item, i) => {
                    const cat = item._category;
                    const meta = CATEGORY_META[cat] || {};
                    const isNav = cat === 'navigation';
                    const label = isNav ? item.label : getItemLabel(item, cat);
                    const subtitle = isNav ? item.path : getItemSubtitle(item, cat);
                    const prefix = isNav ? null : getItemPrefix(item, cat);
                    const status = isNav ? null : getItemStatus(item, cat);
                    return (
                      <div
                        key={`ai-${i}`}
                        onClick={() => selectItem(item)}
                        style={{
                          padding: '7px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9,
                          borderRadius: 6, background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                        }}
                      >
                        <ResultIcon item={item} cat={cat} emoji={isNav ? '🧭' : meta.emoji} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {prefix && <span style={{ fontFamily: 'var(--font-mono)', marginRight: 4, fontSize: 11, color: 'var(--c-text-muted)' }}>{prefix}</span>}
                            {label}
                          </div>
                          {subtitle && (
                            <div style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>
                          )}
                        </div>
                        {status && (
                          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 600, flexShrink: 0, background: `${STATUS_COLORS[status] || '#6b7280'}22`, color: STATUS_COLORS[status] || '#6b7280' }}>{status}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {allItems.length === 0 && results && !loading && hasQuery && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
              {t('cmdk.noResults', { query })}
            </div>
          )}

          {sections.map(section => {
            const isRecents = section.key === 'recents';
            const showCatLabel = isRecents || section.key === 'for_you';
            return (
              <div key={section.key}>
                <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 600, color: section.key === 'for_you' ? 'var(--c-brand)' : 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{section.emoji} {section.label}</span>
                  {isRecents && (
                    <button
                      onClick={(e) => { e.stopPropagation(); localStorage.removeItem(RECENTS_KEY); setRecents([]); }}
                      style={{ background: 'none', border: 'none', color: 'var(--c-text-muted)', fontSize: 10, cursor: 'pointer', padding: 0, textTransform: 'none', letterSpacing: 0 }}
                      title={t('cmdk.clearRecentsTitle')}
                    >{t('cmdk.clearRecents')}</button>
                  )}
                </div>
                {section.items.map(item => {
                  globalIdx++;
                  const idx = globalIdx;
                  const isActive = idx === activeIdx;
                  const cat = item._category;
                  const meta = CATEGORY_META[cat] || {};
                  const prefix = getItemPrefix(item, cat);
                  const subtitle = getItemSubtitle(item, cat);
                  const status = getItemStatus(item, cat);
                  const isSmart = cat === 'smart_actions';
                  return (
                    <div
                      key={`${section.key}-${cat}-${item.id || item.path || idx}`}
                      data-cmd-idx={idx}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      style={{
                        padding: isSmart ? '10px 16px' : '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                        background: isSmart
                          ? (isActive ? 'color-mix(in srgb, var(--c-brand) 18%, var(--c-surface))' : 'color-mix(in srgb, var(--c-brand) 8%, transparent)')
                          : (isActive ? 'var(--c-surface-2)' : 'transparent'),
                        borderLeft: isActive ? '2px solid var(--c-brand)' : (isSmart ? '2px solid color-mix(in srgb, var(--c-brand) 50%, transparent)' : '2px solid transparent'),
                      }}
                    >
                      <ResultIcon item={item} cat={cat} emoji={meta.emoji} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {prefix && <span style={{ fontFamily: 'var(--font-mono)', marginRight: 4, fontSize: 11, color: 'var(--c-text-muted)' }}>{hl(prefix, cat)}</span>}
                          {renderLabel(item, cat)}
                        </div>
                        {(subtitle || showCatLabel) && (
                          <div style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {showCatLabel
                              ? (subtitle ? `${meta.label} · ${subtitle}` : meta.label)
                              : hl(subtitle, cat)}
                          </div>
                        )}
                      </div>
                      {status && (
                        <span style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 600, flexShrink: 0,
                          background: `${STATUS_COLORS[status] || '#6b7280'}22`,
                          color: STATUS_COLORS[status] || '#6b7280',
                        }}>{status}</span>
                      )}
                      {/* "Productive" = this query previously led to a click.
                          A subtle brand dot, same visual weight as the company
                          label dot — enough to tell the two apart at a glance. */}
                      {cat === 'recent_query' && item.productive && (
                        <span
                          title={t('cmdk.recentSearchProductive')}
                          style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--c-brand)', flexShrink: 0 }}
                        />
                      )}
                      {cat === 'companies' && item.label_color && (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.label_color, flexShrink: 0 }} />
                      )}
                    </div>
                  );
                })}
                {section.more > 0 && (
                  <div
                    onClick={() => setExpandedCats(prev => new Set([...prev, section.category]))}
                    style={{
                      padding: '6px 16px 6px 44px', cursor: 'pointer', fontSize: 12,
                      color: 'var(--c-brand)', fontWeight: 600,
                    }}
                  >
                    {t('cmdk.showMore', { count: section.more })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--c-border)', display: 'flex', gap: 12, alignItems: 'center', justifyContent: aiPref.canToggle ? 'space-between' : 'center' }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>↑↓ {t('cmdk.hintNavigate') || 'navigate'}</span>
            <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>↵ {t('cmdk.hintSelect') || 'select'}</span>
            {aiPref.enabled && <span style={{ fontSize: 10, color: 'var(--c-brand)' }}>⌘↵ {t('cmdk.hintAskAi') || 'ask AI'}</span>}
            <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>esc {t('cmdk.hintClose') || 'close'}</span>
          </div>
          {aiPref.canToggle && (
            <button
              onClick={toggleAi}
              title={t('cmdk.ai.toggleHint') || 'Asistente IA en lenguaje natural (beta) — solo vos lo ves'}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 600,
                cursor: 'pointer', borderRadius: 999, padding: '3px 9px',
                border: '1px solid', whiteSpace: 'nowrap',
                background: aiPref.enabled ? 'color-mix(in srgb, var(--c-brand) 14%, transparent)' : 'transparent',
                borderColor: aiPref.enabled ? 'color-mix(in srgb, var(--c-brand) 45%, var(--c-border))' : 'var(--c-border)',
                color: aiPref.enabled ? 'var(--c-brand)' : 'var(--c-text-muted)',
              }}
            >
              <Sparkles size={11} />
              {t('cmdk.ai.beta') || 'IA beta'}: {aiPref.enabled ? (t('cmdk.ai.on') || 'ON') : (t('cmdk.ai.off') || 'OFF')}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {modal}
      {speechSupported && (
        <FeatureSpotlight
          anchorRef={voiceSpot.anchorRef}
          show={voiceSpot.shouldShow}
          onDismiss={voiceSpot.dismiss}
          title={t('cmdk.voice.spotlightTitle')}
          body={t('cmdk.voice.spotlightBody')}
          placement="bottom"
        />
      )}
      {webLoginEntry && (
        <WebLoginHelper
          entryId={webLoginEntry}
          onClose={() => { setWebLoginEntry(null); onClose(); }}
        />
      )}
    </>,
    document.body
  );
}
